import React, {useState, useEffect} from "react";
import {
  Typography,
  Button,
  Grid,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Card, CardContent, Link
} from "@mui/material";
import {useCustomer} from "context/customer/customerContext";
import SideBar from "../../../dealer/Inspection/SideBar";
import TipsCard from "components/ConditionRating/TipsCard";
import HelpCard from "components/ConditionRating/HelpCard";
import {useNavigate} from "react-router-dom";
import {useUI} from "context/ui/uiContext";

import odo_illustration from "assets/illustrations/odo.jpeg";
import left_side_illustration from "assets/illustrations/left_side.jpg";
import right_side_illustration from "assets/illustrations/right_side.jpg";
import rear_illustration from "assets/illustrations/rear.jpg";
import key_fob_illustration from "assets/illustrations/key_fob.jpg";
import gas_cap_illustration from "assets/illustrations/gas_cap.jpg";
import front_tire_illustration from "assets/illustrations/front_tire.jpg";
import rear_tire_illustration from "assets/illustrations/rear_tire.jpg";
import clocks_illustration from "assets/illustrations/clocks.jpg";
import {AccountBoxRounded, LightbulbRounded} from "@mui/icons-material";
import ConditionRating from "components/ConditionRating";
import {useIsMobile} from "hooks/useIsMobile";

const illustrationMap = {
  "Right side": right_side_illustration,
  "Front": front_tire_illustration,
  "Left side": left_side_illustration,
  "Rear": rear_illustration,
  "Odometer": odo_illustration,
  "Oil Pressure": clocks_illustration,
  "Key Fobs": key_fob_illustration,
  "Under Gas Cap": gas_cap_illustration,
  "Front tire tread": front_tire_illustration,
  "Rear tire tread": rear_tire_illustration,
  "VIN": left_side_illustration,
};

const tipsText = {
  0: [
    "Make sure to check all parts of the motorcycle and select the most appropriate condition.",
    "Have any basic tools and a flashlight handy, as you might need them to check certain areas more closely."
  ],
  1:[
    "Make sure to check all parts of the motorcycle and select the most appropriate condition.",
    "Have any basic tools and a flashlight handy, as you might need them to check certain areas more closely."
  ]
  ,
  2: ["Make sure to check all parts of the motorcycle and select the most appropriate condition.",
    "Have any basic tools and a flashlight handy, as you might need them to check certain areas more closely."
  ],
  3: [
    "Ensure the motorcycle is photographed against a neutral background, such as a plain wall or open space, to avoid distractions.",
    "Take photos in a well-lit area, preferably with natural light or with adequate artificial lighting, to capture the true condition of the motorcycle.",
    "Frame the entire motorcycle in the shot for overall condition photos, and take close-ups of any areas that show damage, wear, or unique features.",
    "Hold the camera steady to avoid blurry images. Consider using a tripod or stand if possible.",
    "Capture multiple angles, including front, rear, both sides, and close-ups of the tires, seat, handlebars, and engine.",
    "Take a photo of the odometer to document the current mileage clearly."
  ]
};

/**
 * @param {number} index - The index of the tab which is used to create unique IDs for accessibility attributes.
 * @returns {object} An object containing accessibility attributes (`id` and `aria-controls`) for a tab element.
 */
function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

/**
 * ConditionRating is a functional React component that renders the inspection form for assessing
 * various aspects of a motorcycle's condition. It is organized into tabs that categorize the inspection
 * criteria into mechanical, cosmetic, accessories, and pictures for documentation. The component
 * handles data fetching, navigation, and form state management.
 * @returns {React.Element} A React component that renders a grid layout with side navigation and multiple
 *                        tabs containing form elements for different inspection criteria. Each tab contains
 *                        a list of questions related to the motorcycle's condition, options for responses,
 *                        and the ability to navigate through the tabs or submit the inspection data.
 *
 * Features:
 * - Fetches master data based on customer vehicle details when the component mounts.
 * - Manages state for form values, selected options, and current tab index.
 * - Handles option changes within the form and updates the state accordingly.
 * - Supports navigation to a confirmation or next steps page upon form submission.
 * - Provides a structured layout with side navigation and tabbed content areas for organized data entry.
 *
 * Usage:
 * This component is intended to be used as part of a larger application where vehicle inspection data
 * needs to be collected and processed. It expects to operate within a context where `useCustomer` and
 * `useNavigate` hooks are available to supply necessary data and navigation capabilities.
 */
function Inspection() {
  const {customerDetails, fetchCRMaster, inspection, submitConditionReport, addImage, images, removeImage} = useCustomer();
  const vehicleID = customerDetails?.vehicle?.vehicleId || customerDetails?.vehicle?.vehicleID;
  const navigate = useNavigate();
  const [selectedOptions, setSelectedOptions] = useState({});
  const [mileage, setMileage] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const {showAlert, showLoadingSpinner, hideLoadingSpinner} = useUI();
  const [open, setOpen] = useState(false);
  const [inspectionToBeSubmitted, setInspectionToBeSubmitted] = useState(null);
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
  };

  useEffect(() => {
    fetchInspectionData();
  }, [customerDetails.vehicleModelID]);

  const fetchInspectionData = async () => {
    try {
      await fetchCRMaster(customerDetails.vehicleModelID);
    } catch (e) {
      console.log(e);
    }
  };

  const handleConfirmSubmit = async () => {
    setOpen(false);

    try {
      showLoadingSpinner();
      setLoading(true);
      const {conditionResult, mileage} = inspectionToBeSubmitted;
      const {success, message, returnEligible} = await submitConditionReport({conditionResult, mileage});
      setLoading(false);
      if (!returnEligible) {
        hideLoadingSpinner();
        navigate("/customer/error/ineligible");
        return;
      }

      if (success) {
        hideLoadingSpinner();
        navigate("/customer/delivery");
        return;
      }

      hideLoadingSpinner();
      showAlert(message, "error");
    }
    catch (error) {
      console.log(error);
      hideLoadingSpinner();
      showAlert(error.message, "error");
    }
  };

  const submitInspection = async (conditionResult, mileage) => {
    console.log("submitInspection", conditionResult, mileage);
    setInspectionToBeSubmitted({ conditionResult, mileage });
    handleOpenDialog();
  };

  return (
    <Grid container spacing={isMobile ? 0 : 2} justifyContent="center" alignItems="start"
      sx={{margin: { xs: 0, sm: "25px" }, paddingLeft: { xs: 2, sm: 0 }}}>
      {inspection && <ConditionRating
        tabChangeCallback={setCurrentTab}
        addImage={addImage}
        removeImage={removeImage}
        images={images} vehicle={customerDetails} inspection={inspection} submitConditionReport={submitInspection} isDealer={false}>
        <Grid item xs={12} md={4} sx={{ pr: 2 }}>
          <SideBar unit={{
            year: customerDetails?.year || "N/A",
            make: customerDetails?.brand || "N/A",
            model: customerDetails?.model || "N/A",
            vin: customerDetails?.vin || "N/A",
          }} />


          <TipsCard tipsText={tipsText} currentTab={currentTab} />

          <HelpCard />

        </Grid>
      </ConditionRating>
      }

      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleCloseDialog(event, reason);
          }
        }}
        disableEscapeKeyDown
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Submission"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            The information you have submitted cannot be changed. Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button disabled={loading} onClick={handleConfirmSubmit} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default Inspection;
